/**
 * Корень приложения: инициализация Telegram, сессия, маршруты.
 *
 * Порядок решений: не в Telegram → загрузка → приглашение из deep-link →
 * нет семьи → онбординг → рабочие экраны по роли.
 */

import { useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'

import { NannyShell, ParentShell } from '@/app/RoleShell'
import { useSession } from '@/app/session'
import { InviteAccept } from '@/pages/InviteAccept'
import { OutsideTelegram, Splash } from '@/pages/Splash'
import { Trust } from '@/pages/Trust'
import { Earnings } from '@/pages/nanny/Earnings'
import { MyDay } from '@/pages/nanny/MyDay'
import { ProfileScreen } from '@/pages/nanny/ProfileScreen'
import { NannySchedule } from '@/pages/nanny/Schedule'
import { Onboarding } from '@/pages/onboarding/Onboarding'
import { FamilyScreen } from '@/pages/parent/FamilyScreen'
import { Pay } from '@/pages/parent/Pay'
import { Schedule } from '@/pages/parent/Schedule'
import { Today } from '@/pages/parent/Today'
import { useMe } from '@/shared/api/queries'
import { initAnalytics } from '@/shared/analytics'
import { inviteTokenFromLaunch } from '@/shared/invite'
import { initMiniApp, isTelegram } from '@/shared/telegram'
import { ErrorState } from '@/shared/ui/states'

export function App() {
  const setMe = useSession((state) => state.setMe)
  const [pendingInvite, setPendingInvite] = useState<string | undefined>(inviteTokenFromLaunch)

  useEffect(() => {
    initAnalytics()
    return initMiniApp()
  }, [])

  const inTelegram = isTelegram()
  const { data, error, isLoading, refetch } = useMe({ enabled: inTelegram })

  useEffect(() => {
    if (data) setMe(data)
  }, [data, setMe])

  if (!inTelegram) return <OutsideTelegram />
  if (isLoading) return <Splash />
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />
  if (!data) return <Splash />

  if (pendingInvite) {
    return (
      <InviteAccept
        token={pendingInvite}
        onDone={() => setPendingInvite(undefined)}
        onSkip={() => setPendingInvite(undefined)}
      />
    )
  }

  if (!data.active_family_id) {
    return <Onboarding name={data.user.first_name} onInvite={setPendingInvite} />
  }

  const role = data.memberships.find((m) => m.family_id === data.active_family_id)?.role

  return role === 'nanny' ? (
    <Routes>
      <Route element={<NannyShell />}>
        <Route path="/day" element={<MyDay />} />
        <Route path="/schedule" element={<NannySchedule />} />
        <Route path="/earnings" element={<Earnings />} />
        <Route path="/profile" element={<ProfileScreen />} />
        <Route path="/trust" element={<Trust />} />
      </Route>
      <Route path="*" element={<Navigate to="/day" replace />} />
    </Routes>
  ) : (
    <Routes>
      <Route element={<ParentShell />}>
        <Route path="/today" element={<Today />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/pay" element={<Pay />} />
        <Route path="/family" element={<FamilyScreen />} />
        <Route path="/trust" element={<Trust />} />
      </Route>
      <Route path="*" element={<Navigate to="/today" replace />} />
    </Routes>
  )
}
